import {
  BoxV2 as Box,
  ButtonV2 as Button,
  DropdownV3 as Dropdown,
  Flex,
  TextInputV2 as TextInput,
} from 'portal-commons';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  faBuilding,
  faEnvelope,
  faTags,
  faRegistered,
} from '@fortawesome/pro-regular-svg-icons';

import FormTitle from './FormTitle';
import FormSection from './FormSection';
import { useBrandDetailsControllers } from './hooks';
import { BrandDetailsFormValues } from './types';
import {
  selectCountries,
  selectStockExchanges,
  selectVerticalTypes,
} from '../../../CSP-Form/selectors';
import PhoneInput from '../../../Campaigns/components/RcsCampaignDetailsForm/PhoneInput';
import { GermanyPOCEntityTypeOptions } from '@/constants/options';

interface BrandDetailsFormProps {
  defaultValues?: BrandDetailsFormValues;
  submitLabel?: string;
  proceeding?: boolean;
  onCancel?: () => void;
  onSubmit?: (data: BrandDetailsFormValues) => void;
  onNext?: (data: BrandDetailsFormValues) => void;
  showTitle?: boolean;
  hideActions?: boolean;
  formId?: string;
  onIdentifyingFieldsChange?: (isDirty: boolean) => void;
  onErrorsChange?: (hasErrors: boolean) => void;
  validationMode?: 'onBlur' | 'onChange' | 'onSubmit' | 'onTouched' | 'all';
  onValidatingChange?: (isValidating: boolean) => void;
}

const BrandDetailsForm: FunctionComponent<BrandDetailsFormProps> = ({
  defaultValues,
  submitLabel = 'Next: Trademarks',
  proceeding = false,
  onCancel,
  onSubmit,
  onNext,
  showTitle = true,
  hideActions = false,
  formId,
  onIdentifyingFieldsChange,
  onErrorsChange,
  validationMode = 'onSubmit',
  onValidatingChange,
}) => {
  const {
    control,
    formState: { errors, isValidating },
    handleSubmit,
    watch,
  } = useForm<BrandDetailsFormValues>({
    mode: validationMode,
    defaultValues,
  });

  useEffect(() => {
    onValidatingChange?.(isValidating);
  }, [isValidating, onValidatingChange]);

  useEffect(() => {
    if (!onIdentifyingFieldsChange) return;

    let lastDirty = false;

    const checkDirty = (values: Partial<BrandDetailsFormValues>) => {
      const companyNameChanged =
        (values.companyName ?? '') !== (defaultValues?.companyName ?? '');
      const displayNameChanged =
        (values.displayName ?? '') !== (defaultValues?.displayName ?? '');
      const entityTypeChanged =
        (values.entityType ?? '') !== (defaultValues?.entityType ?? '');
      const einChanged = (values.ein ?? '') !== (defaultValues?.ein ?? '');
      const einIssuingCountryChanged =
        (values.einIssuingCountry ?? '') !==
        (defaultValues?.einIssuingCountry ?? '');

      return (
        companyNameChanged ||
        displayNameChanged ||
        entityTypeChanged ||
        einChanged ||
        einIssuingCountryChanged
      );
    };

    const subscription = watch((value) => {
      const isDirty = checkDirty(value);
      if (isDirty !== lastDirty) {
        lastDirty = isDirty;
        onIdentifyingFieldsChange(isDirty);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, defaultValues, onIdentifyingFieldsChange]);

  const hasErrors = Object.values(errors).some((error) => !!error.message);

  useEffect(() => {
    if (!isValidating) {
      onErrorsChange?.(hasErrors);
    }
  }, [hasErrors, isValidating, onErrorsChange]);
  const {
    // Brand Legal Name
    companyName,
    setCompanyName,
    validateCompanyName,
    companyNameError,
    // Brand DBA Name
    displayName,
    setDisplayName,
    validateDisplayName,
    displayNameError,
    // Entity Type
    entityType,
    setEntityType,
    entityTypeError,
    // Vertical Type
    vertical,
    setVertical,
    verticalError,
    // Stock Symbol
    stockSymbol,
    setStockSymbol,
    validateStockSymbol,
    stockSymbolDisabled,
    stockSymbolError,
    // Stock Exchange
    stockExchange,
    setStockExchange,
    stockExchangeDisabled,
    stockExchangeError,
    // Website / Online Presence
    website,
    setWebsite,
    validateWebsite,
    websiteError,
    // First Name
    firstName,
    setFirstName,
    validateFirstName,
    firstNameDisabled,
    firstNameError,
    // Last Name
    lastName,
    setLastName,
    validateLastName,
    lastNameDisabled,
    lastNameError,
    // Support Phone Number
    phone,
    setPhoneCountry,
    setPhone,
    validatePhone,
    phoneError,
    // Support Email Address
    email,
    setEmail,
    validateEmail,
    emailError,
    // Country of Registration
    country,
    setCountry,
    countryError,
    // Company Registration Number / ID
    ein,
    setEin,
    validateEin,
    einDisabled,
    einError,
    // Number / ID Issuing Country
    einIssuingCountry,
    setEinIssuingCountry,
    validateEinIssuingCountry,
    einIssuingCountryDisabled,
    einIssuingCountryError,
    // Address / Street
    street,
    setStreet,
    validateStreet,
    streetError,
    // City
    city,
    setCity,
    validateCity,
    cityError,
    // State / Region
    state,
    setState,
    validateState,
    stateError,
    // Postal Code
    postalCode,
    setPostalCode,
    validatePostalCode,
    postalCodeError,
    // Business Contact Email
    businessContactEmail,
    setBusinessContactEmail,
    validateBusinessContactEmail,
    businessContactEmailError,
  } = useBrandDetailsControllers(control, []);
  const countries = useSelector(selectCountries);
  const verticalTypes = useSelector(selectVerticalTypes);
  const stockExchanges = useSelector(selectStockExchanges);
  const verticalTypeOptions = useMemo(
    () =>
      verticalTypes.map((item) => ({
        label: item.displayName,
        value: item.id,
      })),
    [verticalTypes]
  );
  const stockExchangeOptions = useMemo(
    () =>
      stockExchanges.map((item) => ({
        label: item,
        value: item,
      })),
    [stockExchanges]
  );

  const handleValid = (data: BrandDetailsFormValues) => {
    if (onSubmit) {
      onSubmit(data);
    } else {
      onNext?.(data);
    }
  };

  // in edit mode, stop syncing DBA name with legal name
  useEffect(() => {
    if (!defaultValues?.displayName) {
      setDisplayName(companyName);
    }
  }, [companyName, defaultValues?.displayName]);

  return (
    <form id={formId} noValidate onSubmit={handleSubmit(handleValid)}>
      <Flex sx={{ flexDirection: 'column', gap: 'xl', width: '100%' }}>
        {showTitle && (
          <FormTitle
            title="Brand Details"
            description={
              'Enter the details needed for your brand. All fields are required unless stated as “Optional”'
            }
          />
        )}
        <FormSection icon={faTags} title="Brand Details" testId="brandDetails">
          <TextInput
            error={!!companyNameError?.message}
            label="Brand Legal Name"
            info="For Brand Legal Name, refer to your official letter from the IRS (CP-575). Enter the text from NAME LINE 1 as your Brand Legal Name. In some instances, you may also need to append the text from NAME LINE 2. The only special characters allowed are hyphens (-) and ampersands (&)."
            note={companyNameError?.message}
            value={companyName}
            onChange={setCompanyName}
            onBlur={validateCompanyName}
            data-testid="companyName"
          />
          <TextInput
            error={!!displayNameError?.message}
            label="Brand DBA Name"
            note={displayNameError?.message}
            value={displayName}
            onChange={setDisplayName}
            onBlur={validateDisplayName}
            data-testid="displayName"
          />
          <Dropdown
            error={!!entityTypeError?.message}
            label="Entity Type"
            note={entityTypeError?.message}
            options={GermanyPOCEntityTypeOptions}
            value={entityType}
            onChange={setEntityType}
            data-testid="entityType"
          />
          <Dropdown
            error={!!verticalError?.message}
            label="Vertical Type"
            note={verticalError?.message}
            options={verticalTypeOptions}
            value={vertical}
            onChange={setVertical}
            data-testid="vertical"
          />
          {!stockSymbolDisabled && (
            <TextInput
              error={!!stockSymbolError?.message}
              label="Stock Symbol (Optional)"
              note={stockSymbolError?.message}
              value={stockSymbol}
              onChange={setStockSymbol}
              onBlur={validateStockSymbol}
              data-testid="stockSymbol"
            />
          )}
          {!stockExchangeDisabled && (
            <Dropdown
              error={!!stockExchangeError?.message}
              label="Stock Exchange (Optional)"
              note={stockExchangeError?.message}
              options={stockExchangeOptions}
              value={stockExchange}
              onChange={setStockExchange}
              data-testid="stockExchange"
            />
          )}
          <TextInput
            error={!!websiteError?.message}
            label="Website / Online Presence"
            note={websiteError?.message}
            value={website}
            onChange={setWebsite}
            onBlur={validateWebsite}
            data-testid="website"
          />
          <Box />
          {!firstNameDisabled && (
            <TextInput
              error={!!firstNameError?.message}
              label="First Name"
              note={firstNameError?.message}
              value={firstName}
              onChange={setFirstName}
              onBlur={validateFirstName}
              data-testid="firstName"
            />
          )}
          {!lastNameDisabled && (
            <TextInput
              error={!!lastNameError?.message}
              label="Last Name"
              note={lastNameError?.message}
              value={lastName}
              onChange={setLastName}
              onBlur={validateLastName}
              data-testid="lastName"
            />
          )}
          <PhoneInput
            error={!!phoneError?.message}
            placeholder="Support Phone Number"
            note={phoneError?.message}
            value={phone}
            onCountryChange={setPhoneCountry}
            onChange={setPhone}
            onBlur={validatePhone}
            data-testid="phone"
          />
          <TextInput
            error={!!emailError?.message}
            label="Support Email Address"
            note={emailError?.message}
            value={email}
            onChange={setEmail}
            onBlur={validateEmail}
            data-testid="email"
          />
        </FormSection>
        <FormSection
          icon={faRegistered}
          title="Registration Information"
          testId="registrationInformation"
        >
          <Dropdown
            error={!!countryError?.message}
            label="Country of Registration"
            note={countryError?.message}
            options={countries}
            value={country}
            onChange={setCountry}
            data-testid="country"
          />
          <Box />
          {!einDisabled && (
            <TextInput
              error={!!einError?.message}
              label="Company Registration Number / ID"
              note={einError?.message}
              value={ein}
              onChange={setEin}
              onBlur={validateEin}
              data-testid="ein"
            />
          )}
          {!einIssuingCountryDisabled && (
            <Dropdown
              error={!!einIssuingCountryError?.message}
              label="Number / ID Issuing Country"
              note={einIssuingCountryError?.message}
              options={countries}
              value={einIssuingCountry}
              onChange={setEinIssuingCountry}
              onBlur={validateEinIssuingCountry}
              data-testid="einIssuingCountry"
            />
          )}
        </FormSection>
        <FormSection
          icon={faBuilding}
          title="Company Address"
          testId="companyAddress"
        >
          <TextInput
            style={{ gridColumn: 'span 2' }}
            error={!!streetError?.message}
            label="Address / Street"
            note={streetError?.message}
            value={street}
            onChange={setStreet}
            onBlur={validateStreet}
            data-testid="street"
          />
          <TextInput
            error={!!cityError?.message}
            label="City"
            note={cityError?.message}
            value={city}
            onChange={setCity}
            onBlur={validateCity}
            data-testid="city"
          />
          <TextInput
            error={!!stateError?.message}
            label="State / Region"
            note={stateError?.message}
            value={state}
            onChange={setState}
            onBlur={validateState}
            data-testid="state"
          />
          <TextInput
            error={!!postalCodeError?.message}
            label="Postal Code"
            note={postalCodeError?.message}
            value={postalCode}
            onChange={setPostalCode}
            onBlur={validatePostalCode}
            data-testid="postalCode"
          />
          <TextInput
            readOnly
            label="Country"
            value={
              countries.find((option) => option.value === country)?.label ?? ''
            }
            data-testid="country"
          />
        </FormSection>
        <FormSection
          icon={faEnvelope}
          title="Contact Information"
          description={
            "Brand's business contact will need to complete a Two-Factor Authentication (2FA)\nsent to their email address for brand verification."
          }
          testId="contactInformation"
        >
          <TextInput
            error={!!businessContactEmailError?.message}
            label="Business Contact Email"
            note={businessContactEmailError?.message}
            value={businessContactEmail}
            onChange={setBusinessContactEmail}
            onBlur={validateBusinessContactEmail}
            data-testid="businessContactEmail"
          />
        </FormSection>
        {!hideActions && (
          <Flex sx={{ justifyContent: 'flex-end', gap: 'm' }}>
            <Button
              shape="square"
              variant="outline"
              size="lg"
              color="secondary"
              disabled={proceeding}
              onClick={onCancel}
              data-testid="cancel"
            >
              Cancel
            </Button>
            <Button
              disabled={
                proceeding ||
                isValidating ||
                Object.values(errors).some((error) => !!error.message)
              }
              shape="square"
              type="submit"
              size="lg"
              color="secondary"
              data-testid="submit"
            >
              {submitLabel}
            </Button>
          </Flex>
        )}
      </Flex>
    </form>
  );
};

export default BrandDetailsForm;
